import { useMemo, useState } from 'react';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardBody,
  Button,
  Skeleton,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
  Textarea,
} from '@vnetwork/ui/primitives';
import { DataTable, Pagination, Stat, EmptyState, StatusPill, type ColumnDef } from '@vnetwork/ui/data';
import { useToast } from '@vnetwork/ui/feedback';
import {
  useAffiliateProfile,
  useAffiliateStats,
  useAffiliatePayouts,
  useRequestPayout,
  formatCurrency,
  statusTone,
  type AffiliatePayout,
} from '@/api/affiliate';
import { errorMessage } from '@/api/error-message';
import { formatDate } from '@/lib/format-date';
import styles from './Affiliate.module.css';

const PAGE_SIZE = 10;

export function AffiliatePayouts() {
  const { show } = useToast();
  const [page, setPage] = useState(1);
  const [requestOpen, setRequestOpen] = useState(false);

  const { data: profile } = useAffiliateProfile();
  const { data: stats } = useAffiliateStats(!!profile);
  const { data: payouts, isLoading, isError } = useAffiliatePayouts(page, PAGE_SIZE, !!profile);

  const available = stats?.pending_commission ?? profile?.unpaid_commission ?? 0;
  const paidOut = stats?.paid_commission ?? 0;
  const threshold = profile?.payout_threshold ?? 0;
  const canRequest = available >= threshold && available > 0;

  const cols = useMemo<ColumnDef<AffiliatePayout, unknown>[]>(
    () => [
      {
        id: 'period',
        header: 'Period',
        cell: ({ row }) =>
          `${formatDate(row.original.period_start)} – ${formatDate(row.original.period_end)}`,
      },
      {
        id: 'amount',
        header: 'Amount',
        cell: ({ row }) => formatCurrency(row.original.amount, row.original.currency),
      },
      {
        id: 'method',
        header: 'Method',
        cell: ({ row }) => row.original.payout_method || '—',
      },
      {
        id: 'status',
        header: 'Status',
        enableSorting: false,
        cell: ({ row }) => (
          <StatusPill tone={statusTone(row.original.status)}>
            {row.original.status}
          </StatusPill>
        ),
      },
      {
        id: 'requested',
        header: 'Requested',
        cell: ({ row }) => formatDate(row.original.requested_at),
      },
    ],
    [],
  );

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-5)' }}>
      <div className={styles.payoutStatGrid}>
        <Card flat>
          <CardBody>
            <Stat label="Available to withdraw" value={formatCurrency(available)} />
          </CardBody>
        </Card>
        <Card flat>
          <CardBody>
            <Stat label="Total paid out" value={formatCurrency(paidOut)} />
          </CardBody>
        </Card>
        <Card flat>
          <CardBody>
            <Stat label="Payout threshold" value={formatCurrency(threshold)} />
          </CardBody>
        </Card>
      </div>

      <Card>
        <CardHeader
          actions={
            <Button size="sm" disabled={!canRequest} onClick={() => setRequestOpen(true)}>
              Request payout
            </Button>
          }
        >
          <CardTitle>Payout history</CardTitle>
          <CardDescription>
            {canRequest
              ? 'You have enough commission to request a payout.'
              : `You need ${formatCurrency(threshold)} available before requesting a payout.`}
          </CardDescription>
        </CardHeader>
        <CardBody>
          {isLoading ? (
            <Skeleton style={{ height: 120 }} />
          ) : isError ? (
            <EmptyState title="Could not load payouts" description="Something went wrong. Please try again." />
          ) : (payouts?.data?.length ?? 0) === 0 ? (
            <EmptyState title="No payouts yet" description="Your payout requests will appear here." />
          ) : (
            <>
              <DataTable
                columns={cols}
                data={payouts?.data ?? []}
                getRowId={(r) => String(r.payout_id)}
              />
              {payouts && payouts.total > PAGE_SIZE && (
                <div style={{ marginTop: 'var(--vn-space-4)' }}>
                  <Pagination
                    page={page}
                    pageCount={Math.ceil(payouts.total / PAGE_SIZE)}
                    onPageChange={setPage}
                    summary={`${payouts.total} payouts`}
                  />
                </div>
              )}
            </>
          )}
        </CardBody>
      </Card>

      <RequestPayoutDialog
        open={requestOpen}
        onOpenChange={setRequestOpen}
        available={available}
        onDone={() => show({ tone: 'success', title: 'Payout requested' })}
      />
    </div>
  );
}

function RequestPayoutDialog({
  open,
  onOpenChange,
  available,
  onDone,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  available: number;
  onDone: () => void;
}) {
  const { show } = useToast();
  const [notes, setNotes] = useState('');
  const request = useRequestPayout({
    onSuccess: () => {
      onDone();
      onOpenChange(false);
      setNotes('');
    },
    onError: (e) =>
      show({
        tone: 'danger',
        title: 'Could not request payout',
        description: errorMessage(e, 'Please try again.'),
      }),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>Request payout</DialogTitle>
          <DialogDescription>
            Withdraw your available commission of {formatCurrency(available)}.
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          <Textarea
            rows={3}
            placeholder="Notes for the finance team (optional)…"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button disabled={request.isPending} onClick={() => request.mutate({ notes: notes.trim() || undefined })}>
            {request.isPending ? 'Requesting…' : 'Request payout'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
